/**
 * User Detail component with edit form and submissions
 */
import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { useTheme } from '../../context/ThemeContext';
import '../../styles/themes.css';

const UserDetail = ({ user: userProp, onBack }) => {
  const { updateUser, updateUserVisibility } = useAdmin();
  const { theme } = useTheme();

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    is_admin: false,
    show_on_leaderboard: true
  });

  useEffect(() => {
    if (userProp && userProp.user) {
      setFormData({
        email: userProp.user.email || '',
        password: '',
        is_admin: userProp.user.is_admin || false,
        show_on_leaderboard: userProp.user.show_on_leaderboard !== undefined ? userProp.user.show_on_leaderboard : true
      });
    }
  }, [userProp]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userProp || !userProp.user) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Only send fields that have changed
      const updates = {};

      if (formData.email !== userProp.user.email) {
        updates.email = formData.email;
      }

      if (formData.password) {
        updates.password = formData.password;
      }

      if (formData.is_admin !== userProp.user.is_admin) {
        updates.is_admin = formData.is_admin;
      }

      // Handle leaderboard visibility separately
      const visibilityChanged = formData.show_on_leaderboard !== userProp.user.show_on_leaderboard;

      if (Object.keys(updates).length === 0 && !visibilityChanged) {
        setError('No changes to save');
        return;
      }

      // Update user data if there are field changes
      if (Object.keys(updates).length > 0) {
        await updateUser(userProp.user.id, updates);
      }

      // Update leaderboard visibility if changed
      if (visibilityChanged) {
        await updateUserVisibility(userProp.user.id, formData.show_on_leaderboard);
      }

      setSuccess('User updated successfully');
      setEditing(false);
      setFormData(prev => ({ ...prev, password: '' })); // Clear password field
    } catch (err) {
      setError('Failed to update user: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setError('');
    setSuccess('');
    // Reset form data
    if (userProp && userProp.user) {
      setFormData({
        email: userProp.user.email || '',
        password: '',
        is_admin: userProp.user.is_admin || false,
        show_on_leaderboard: userProp.user.show_on_leaderboard !== undefined ? userProp.user.show_on_leaderboard : true
      });
    }
  };

  if (!userProp || !userProp.user) {
    return (
      <div className="themed-background" style={{
        textAlign: 'center',
        padding: '2rem',
        minHeight: '100vh'
      }}>
        <p style={{ color: 'var(--color-background-text)' }}>No user selected</p>
        <button onClick={onBack} className="themed-button">Back to Users</button>
      </div>
    );
  }

  const { user, submissions } = userProp;

  return (
    <div className="themed-background" style={{ padding: '1rem', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <h2 style={{ color: 'var(--color-background-text)' }}>User Details</h2>
        <button
          onClick={onBack}
          className="themed-button"
          style={{ padding: '0.5rem 1rem' }}
        >
          ← Back to Users
        </button>
      </div>

      {/* User Information Card */}
      <div className="themed-surface" style={{
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px var(--shadow-color)',
        marginBottom: '2rem'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem'
        }}>
          <h3 style={{ color: 'var(--color-surface-text)' }}>User Information</h3>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="themed-button"
              style={{ padding: '0.5rem 1rem' }}
            >
              Edit User
            </button>
          )}
        </div>

        {error && (
          <div className="status-error themed-surface" style={{
            padding: '0.5rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid var(--color-error)'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div className="status-success themed-surface" style={{
            padding: '0.5rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid var(--color-success)'
          }}>
            {success}
          </div>
        )}

        {editing ? (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: 'var(--color-surface-text)',
                fontWeight: 'bold'
              }}>
                Email:
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="themed-input"
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: 'var(--color-surface-text)',
                fontWeight: 'bold'
              }}>
                New Password (leave blank to keep current):
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                minLength="8"
                className="themed-input"
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: 'var(--color-surface-text)'
              }}>
                <input
                  type="checkbox"
                  name="is_admin"
                  checked={formData.is_admin}
                  onChange={handleInputChange}
                />
                Administrator privileges
              </label>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: 'var(--color-surface-text)'
              }}>
                <input
                  type="checkbox"
                  name="show_on_leaderboard"
                  checked={formData.show_on_leaderboard}
                  onChange={handleInputChange}
                />
                Show on leaderboard
              </label>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                type="submit"
                disabled={loading}
                className="themed-button"
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'var(--color-success)',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="themed-button"
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'var(--color-outline)'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div style={{ color: 'var(--color-surface-text)' }}>
            <div style={{ marginBottom: '1rem' }}>
              <strong>Name:</strong> {
                [user.first_name, user.last_name]
                  .filter(Boolean)
                  .join(' ') || 'Name not provided'
              }
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <strong>Email:</strong> {user.email}
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <strong>Admin Status:</strong>
              <span style={{
                marginLeft: '0.5rem',
                padding: '0.25rem 0.5rem',
                borderRadius: '12px',
                fontSize: '0.75rem',
                backgroundColor: user.is_admin ? 'var(--color-success)' : 'var(--color-warning)',
                color: 'var(--color-primary-text)',
                opacity: 0.9
              }}>
                {user.is_admin ? 'Administrator' : 'Regular User'}
              </span>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <strong>Leaderboard Visibility:</strong>
              <span style={{
                marginLeft: '0.5rem',
                padding: '0.25rem 0.5rem',
                borderRadius: '12px',
                fontSize: '0.75rem',
                backgroundColor: user.show_on_leaderboard ? 'var(--color-success)' : 'var(--color-warning)',
                color: 'var(--color-primary-text)',
                opacity: 0.9
              }}>
                {user.show_on_leaderboard ? 'Visible on Leaderboard' : 'Hidden from Leaderboard'}
              </span>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <strong>Created:</strong> {new Date(user.created_at).toLocaleString()}
            </div>
            <div>
              <strong>User ID:</strong> {user.id}
            </div>
          </div>
        )}
      </div>

      {/* Submissions Section */}
      <div className="themed-surface" style={{
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px var(--shadow-color)'
      }}>
        <h3 style={{
          marginBottom: '1rem',
          color: 'var(--color-surface-text)'
        }}>
          Submissions ({submissions ? submissions.length : 0})
        </h3>

        {submissions && submissions.length > 0 ? (
          <table className="themed-table">
            <thead>
              <tr>
                <th>Exercise</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map(submission => (
                <tr key={submission.id}>
                  <td>
                    {submission.exercise_title || `Exercise ${submission.exercise_id}`}
                  </td>
                  <td>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      backgroundColor: submission.status === 'completed' ? 'var(--color-success)' : 'var(--color-warning)',
                      color: 'var(--color-primary-text)',
                      opacity: 0.9
                    }}>
                      {submission.status}
                    </span>
                  </td>
                  <td>
                    {new Date(submission.created_at).toLocaleString()}
                  </td>
                  <td>
                    <button
                      onClick={() => {
                        // TODO: Implement code viewer modal
                        alert('Code: ' + submission.code.substring(0, 100) + '...');
                      }}
                      className="themed-button"
                      style={{
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        backgroundColor: 'var(--color-accent)'
                      }}
                    >
                      View Code
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{
            color: 'var(--color-surface-text)',
            fontStyle: 'italic',
            opacity: 0.7
          }}>
            No submissions found for this user.
          </p>
        )}
      </div>
    </div>
  );
};

export default UserDetail;